<template>
  <div class="toast-host" aria-live="polite" aria-atomic="false">
    <TransitionGroup name="toast" tag="div" class="toast-host__list">
      <div
        v-for="item in toast.state.items"
        :key="item.id"
        class="toast-host__item"
        :class="`toast-host__item--${item.type}`"
        :role="item.type === 'error' ? 'alert' : 'status'"
        @click="toast.hide(item.id)"
      >
        <AppIcon :name="ICONS[item.type]" :size="20" class="toast-host__icon" />
        <span class="toast-host__text">{{ item.text }}</span>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
/**
 * ToastHost — область глобальных тостов (монтируется один раз в App.vue).
 * Показ: useToast().show(text, {type, timeout}) — см. src/composables/useToast.js. Тап закрывает тост.
 */
import AppIcon from "./AppIcon.vue";
import { useToast } from "@/composables/useToast";

const toast = useToast();

const ICONS = { info: "info", success: "check", error: "warning" };
</script>

<style scoped>
.toast-host {
  position: fixed;
  top: calc(var(--cb-safe-top) + 12px);
  left: 0;
  right: 0;
  z-index: var(--cb-z-toast);
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding: 0 var(--cb-gutter);
}

.toast-host__list {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: calc(var(--cb-app-max) - 2 * var(--cb-gutter));
}

.toast-host__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  border-radius: var(--cb-radius-md);
  background: rgba(24, 29, 22, 0.94);
  border: 1px solid var(--cb-border-strong);
  box-shadow: var(--cb-shadow-pop);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  color: var(--cb-text);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
  pointer-events: auto;
  cursor: pointer;
}

.toast-host__icon {
  color: var(--cb-text-2);
}

.toast-host__item--success {
  border-color: var(--cb-lime-border);
  box-shadow: var(--cb-shadow-pop), var(--cb-glow-sm);
}

.toast-host__item--success .toast-host__icon {
  color: var(--cb-lime);
}

.toast-host__item--error {
  border-color: rgba(255, 107, 90, 0.55);
}

.toast-host__item--error .toast-host__icon {
  color: var(--cb-danger);
}

.toast-host__text {
  flex: 1 1 auto;
  min-width: 0;
}

.toast-enter-active,
.toast-leave-active {
  transition: opacity var(--cb-dur) var(--cb-ease), transform var(--cb-dur) var(--cb-ease);
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(-12px) scale(0.98);
}

.toast-leave-active {
  position: absolute;
}

.toast-move {
  transition: transform var(--cb-dur) var(--cb-ease);
}
</style>
